import { useMemo } from 'react';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { r } from '@/theme/tokens';
import { Stack } from '@/ui/layout/Stack';
import { Box, Popover } from '@/ui/primitives';
import {
  buildDropdownFiltersPopoverStackSx,
  buildDropdownFiltersPopoverPaperSx,
  buildFilterPopoverRootSx,
  dropdownFiltersPopoverBodySx,
  dropdownFiltersPopoverControlWidthPx,
  dropdownFiltersPopoverFieldGap,
  dropdownFiltersPopoverFieldInputPadding,
  dropdownFiltersPopoverFieldsStackSx,
  dropdownFiltersPopoverFontSizePx,
  filterPopoverInsetBackgroundColor,
} from './filterControlStyles';
import { withStatusFilterOptionIcons } from './statusFilterOptionIcons';
import { TagsFilterField } from './TagsFilterField';
import type { PopoverProps } from '@/ui/primitives';
import type { ReactElement } from 'react';

type DropdownFilterOption = {
  value: string;
  label: string;
  count?: number;
};

type DropdownFiltersPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  selectedStatuses: string[];
  onStatusesChange: (next: string[]) => void;
  statusOptions: DropdownFilterOption[];
  statusLabel?: string;
  selectedEntities?: string[];
  onEntitiesChange?: (next: string[]) => void;
  entityOptions?: DropdownFilterOption[];
  entityLabel?: string;
  selectedItems?: string[];
  onItemsChange?: (next: string[]) => void;
  itemOptions?: DropdownFilterOption[];
  itemLabel?: string;
  selectedSuites?: string[];
  onSuitesChange?: (next: string[]) => void;
  suiteOptions?: DropdownFilterOption[];
  suiteLabel?: string;
  selectedTags: string[];
  onTagsChange: (next: string[]) => void;
  tagOptions: DropdownFilterOption[];
  selectedProjects?: string[];
  onProjectsChange?: (next: string[]) => void;
  projectOptions?: DropdownFilterOption[];
  projectLabel?: string;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  transformOrigin?: PopoverProps['transformOrigin'];
  controlRadius?: string;
};

const isFilterPopperCloseEvent = (
  event: Parameters<NonNullable<PopoverProps['onClose']>>[0],
): boolean => {
  if (!('target' in event)) {
    return false;
  }
  const target = event.target;
  return (
    target instanceof Element &&
    target.closest(
      '[data-flowqa-filter-popper="true"], .MuiAutocomplete-popper, .MuiAutocomplete-paper, .MuiAutocomplete-listbox',
    ) !== null
  );
};

export const DropdownFiltersPopover = ({
  open,
  anchorEl,
  onClose,
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  statusLabel = 'Latest Status',
  selectedEntities,
  onEntitiesChange,
  entityOptions,
  entityLabel = 'Item',
  selectedItems,
  onItemsChange,
  itemOptions,
  itemLabel = 'Item',
  selectedSuites,
  onSuitesChange,
  suiteOptions,
  suiteLabel = 'Suite',
  selectedTags,
  onTagsChange,
  tagOptions,
  selectedProjects,
  onProjectsChange,
  projectOptions,
  projectLabel = getFilterLabel('project'),
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  controlRadius = r('sm'),
}: DropdownFiltersPopoverProps): ReactElement => {
  const hasSuiteFilter =
    selectedSuites !== undefined && onSuitesChange !== undefined && suiteOptions !== undefined;
  const hasEntityFilter =
    selectedEntities !== undefined && onEntitiesChange !== undefined && entityOptions !== undefined;
  const hasItemFilter =
    selectedItems !== undefined && onItemsChange !== undefined && itemOptions !== undefined;
  const hasProjectFilter =
    selectedProjects !== undefined &&
    onProjectsChange !== undefined &&
    projectOptions !== undefined;
  const showStatusFilter = statusOptions.length > 0;
  const statusOptionsWithIcons = useMemo(
    () => withStatusFilterOptionIcons(statusOptions),
    [statusOptions],
  );
  const handleClose: NonNullable<PopoverProps['onClose']> = (event) => {
    if (isFilterPopperCloseEvent(event)) {
      return;
    }
    onClose();
  };
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={buildFilterPopoverRootSx(controlRadius, 'visible !important')}
      paperSx={buildDropdownFiltersPopoverPaperSx(controlRadius)}
    >
      <Stack gap="none" sx={buildDropdownFiltersPopoverStackSx(controlRadius)}>
        <Box sx={dropdownFiltersPopoverBodySx}>
          <Stack gap="md" sx={dropdownFiltersPopoverFieldsStackSx}>
            {hasSuiteFilter ? (
              <TagsFilterField
                label={suiteLabel}
                fontSizePx={dropdownFiltersPopoverFontSizePx}
                labelControlGap={dropdownFiltersPopoverFieldGap}
                inputAriaLabel={`Filter by ${suiteLabel.toLowerCase()}`}
                value={selectedSuites}
                options={suiteOptions}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={dropdownFiltersPopoverControlWidthPx}
                inputBackgroundColor={filterPopoverInsetBackgroundColor}
                dropdownBackgroundColor={filterPopoverInsetBackgroundColor}
                inputLeftPadding={dropdownFiltersPopoverFieldInputPadding}
                controlRadius={controlRadius}
                onChange={onSuitesChange}
              />
            ) : null}
            {hasEntityFilter ? (
              <TagsFilterField
                label={entityLabel}
                fontSizePx={dropdownFiltersPopoverFontSizePx}
                labelControlGap={dropdownFiltersPopoverFieldGap}
                inputAriaLabel={`Filter by ${entityLabel.toLowerCase()}`}
                value={selectedEntities}
                options={entityOptions}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={dropdownFiltersPopoverControlWidthPx}
                inputBackgroundColor={filterPopoverInsetBackgroundColor}
                dropdownBackgroundColor={filterPopoverInsetBackgroundColor}
                inputLeftPadding={dropdownFiltersPopoverFieldInputPadding}
                controlRadius={controlRadius}
                onChange={onEntitiesChange}
              />
            ) : null}
            {hasItemFilter ? (
              <TagsFilterField
                label={itemLabel}
                fontSizePx={dropdownFiltersPopoverFontSizePx}
                labelControlGap={dropdownFiltersPopoverFieldGap}
                inputAriaLabel={`Filter by ${itemLabel.toLowerCase()}`}
                value={selectedItems}
                options={itemOptions}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={dropdownFiltersPopoverControlWidthPx}
                inputBackgroundColor={filterPopoverInsetBackgroundColor}
                dropdownBackgroundColor={filterPopoverInsetBackgroundColor}
                inputLeftPadding={dropdownFiltersPopoverFieldInputPadding}
                controlRadius={controlRadius}
                onChange={onItemsChange}
              />
            ) : null}
            {showStatusFilter ? (
              <TagsFilterField
                label={statusLabel}
                fontSizePx={dropdownFiltersPopoverFontSizePx}
                labelControlGap={dropdownFiltersPopoverFieldGap}
                inputAriaLabel={`Filter by ${statusLabel.toLowerCase()}`}
                value={selectedStatuses}
                options={statusOptionsWithIcons}
                onChange={onStatusesChange}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={dropdownFiltersPopoverControlWidthPx}
                inputBackgroundColor={filterPopoverInsetBackgroundColor}
                dropdownBackgroundColor={filterPopoverInsetBackgroundColor}
                inputLeftPadding={dropdownFiltersPopoverFieldInputPadding}
                controlRadius={controlRadius}
              />
            ) : null}
            <TagsFilterField
              label={getFilterLabel('tags')}
              fontSizePx={dropdownFiltersPopoverFontSizePx}
              labelControlGap={dropdownFiltersPopoverFieldGap}
              inputAriaLabel="Filter by tags"
              value={selectedTags}
              options={tagOptions}
              onChange={onTagsChange}
              emptyLabel={
                tagOptions.length > 0 ? getFilterEmptyLabel('all') : getFilterEmptyLabel('tags')
              }
              controlWidthPx={dropdownFiltersPopoverControlWidthPx}
              inputBackgroundColor={filterPopoverInsetBackgroundColor}
              dropdownBackgroundColor={filterPopoverInsetBackgroundColor}
              inputLeftPadding={dropdownFiltersPopoverFieldInputPadding}
              controlRadius={controlRadius}
              optionPresentation="tagChip"
            />
            {hasProjectFilter ? (
              <TagsFilterField
                label={projectLabel}
                fontSizePx={dropdownFiltersPopoverFontSizePx}
                labelControlGap={dropdownFiltersPopoverFieldGap}
                inputAriaLabel={`Filter by ${projectLabel.toLowerCase()}`}
                value={selectedProjects}
                options={projectOptions}
                onChange={onProjectsChange}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={dropdownFiltersPopoverControlWidthPx}
                inputBackgroundColor={filterPopoverInsetBackgroundColor}
                dropdownBackgroundColor={filterPopoverInsetBackgroundColor}
                inputLeftPadding={dropdownFiltersPopoverFieldInputPadding}
                controlRadius={controlRadius}
              />
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Popover>
  );
};

export type { DropdownFilterOption, DropdownFiltersPopoverProps };
